/**
 * Data & Sync Tab
 *
 * Sync mode, provider API keys, views management, maintenance.
 */

import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Cloud,
  CloudOff,
  Check,
  Loader2,
  AlertCircle,
  RefreshCw,
  Trash2,
  Eye,
  EyeOff,
  LayoutGrid,
} from 'lucide-react';
import { CullBacklogModal } from '../../CullBacklogModal';
import {
  getSyncMode,
  setSyncMode,
  detectOrphans,
  applyMergeDecisions,
  syncTasks,
  updateView,
  getProviders,
} from '../../../api/client';
import { useSettings } from '../../../hooks/useSettings';
import { useViews } from '../../../hooks/useViews';
import { MergeConflictsModal } from '../../MergeConflictsModal';
import { DeleteViewConfirmModal } from '../../DeleteViewConfirmModal';
import { ViewIconPicker } from '../../ViewIconPicker';
import { PasswordInput } from '../../ui/PasswordInput';
import { getViewIconComponent } from '../../../utils/viewIcons';
import type {
  SyncMode,
  OrphanedTasksReport,
  MergeDecision,
  View,
  ProviderInfo,
  ProviderId,
} from '../../../types';

export function DataSyncTab() {
  const { clearCache } = useSettings();
  const queryClient = useQueryClient();
  const {
    views,
    toggleHideView,
    isViewHidden,
    deleteView: deleteViewFromHook,
  } = useViews();

  // Provider and sync mode state
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [syncMode, setSyncModeState] = useState<SyncMode | null>(null);
  const [viewToDelete, setViewToDelete] = useState<View | null>(null);
  const [isDeletingView, setIsDeletingView] = useState(false);
  const [iconPickerViewSlug, setIconPickerViewSlug] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [providerApiKey, setProviderApiKey] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<ProviderId | null>(null);
  const [orphans, setOrphans] = useState<OrphanedTasksReport | null>(null);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCullOpen, setIsCullOpen] = useState(false);

  useEffect(() => {
    loadProvidersAndSyncMode();
  }, []);

  const loadProvidersAndSyncMode = async () => {
    setIsLoading(true);
    try {
      const [providerList, mode] = await Promise.all([
        getProviders(),
        getSyncMode(),
      ]);
      setProviders(providerList);
      setSyncModeState(mode);
    } catch (err: unknown) {
      setError(`Failed to load settings: ${(err as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getProviderApiKeyHelpUrl = (providerId: ProviderId): string => {
    switch (providerId) {
      case 'todoist':
        return 'https://todoist.com/prefs/integrations';
      case 'asana':
        return 'https://app.asana.com/0/my-apps';
      default:
        return '';
    }
  };

  const handleToggleSync = async (newMode: ProviderId) => {
    const provider = providers.find((p) => p.id === newMode);

    if (newMode !== 'standalone' && !providerApiKey.trim()) {
      if (syncMode?.mode === newMode && syncMode?.providerApiKeySet) {
        // Already configured
      } else {
        setError(`Please enter your ${provider?.name || newMode} API key`);
        return;
      }
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (newMode !== 'standalone' && syncMode?.mode === 'standalone') {
        const orphanReport = await detectOrphans();
        if (orphanReport.requiresUserDecision) {
          setOrphans(orphanReport);
          setSelectedProvider(newMode);
          setShowMergeModal(true);
          setIsSaving(false);
          return;
        }
      }

      await setSyncMode(newMode, newMode !== 'standalone' ? providerApiKey : undefined);
      await loadProvidersAndSyncMode();
      setSuccess(`Sync mode changed to ${provider?.name || newMode}`);
      setProviderApiKey('');
      setSelectedProvider(null);
    } catch (err: unknown) {
      setError(`Failed to update sync mode: ${(err as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMergeDecisions = async (decisions: MergeDecision[]) => {
    try {
      await applyMergeDecisions(decisions);
      setShowMergeModal(false);
      setOrphans(null);

      if (selectedProvider) {
        await setSyncMode(selectedProvider, providerApiKey);
        await loadProvidersAndSyncMode();
        const provider = providers.find((p) => p.id === selectedProvider);
        setSuccess(`Sync mode changed to ${provider?.name || selectedProvider} and merge decisions applied`);
        setProviderApiKey('');
        setSelectedProvider(null);
      }
    } catch (err: unknown) {
      setError(`Failed to apply merge decisions: ${(err as Error).message}`);
    }
  };

  const handleSyncNow = async () => {
    setIsSyncing(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await syncTasks();
      setSuccess(`Synced ${result.synced} tasks in ${result.duration}ms`);
    } catch (err: unknown) {
      setError(`Sync failed: ${(err as Error).message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClearCache = () => {
    if (confirm('Are you sure you want to clear the cache? This will reload the page.')) {
      clearCache();
    }
  };

  const handleDeleteView = async () => {
    if (!viewToDelete) return;
    setIsDeletingView(true);
    try {
      await deleteViewFromHook(viewToDelete.slug);
      setViewToDelete(null);
      setSuccess(`Deleted view: ${viewToDelete.name}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: unknown) {
      setError(`Failed to delete view: ${(err as Error).message}`);
    } finally {
      setIsDeletingView(false);
    }
  };

  const handleIconSelect = async (view: View, iconName: string) => {
    try {
      await updateView(view.slug, {
        filterConfig: { ...view.filterConfig, icon: iconName },
      });
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('danny-views-updated'));
      }, 0);
      setIconPickerViewSlug(null);
      setSuccess(`Updated icon for "${view.name}"`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: unknown) {
      setError(`Failed to update icon: ${(err as Error).message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  const sortedProviders = [...providers].sort((a, b) => {
    if (a.id === 'standalone') return -1;
    if (b.id === 'standalone') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
        </div>
      )}

      {/* Sync Mode Section — coming soon */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4 opacity-50 pointer-events-none">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Sync Mode
          </h2>
          <span className="px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-widest bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
            Coming Soon
          </span>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Choose how Danny syncs your tasks. Standalone keeps everything local, or connect to an external task provider for bi-directional sync.
        </p>

        <div className="space-y-3">
          {sortedProviders.map((provider) => {
            const isActive = syncMode?.mode === provider.id;
            const isStandalone = provider.id === 'standalone';
            const needsApiKey = !isStandalone && (!isActive || !syncMode?.providerApiKeySet);

            return (
              <div
                key={provider.id}
                className={`p-4 border-2 rounded-lg transition-all ${
                  isActive
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-border-subtle-light dark:border-border-subtle-dark'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      isActive
                        ? 'bg-primary-100 dark:bg-primary-800'
                        : 'bg-surface-hover-light dark:bg-surface-hover-dark'
                    }`}
                  >
                    {isStandalone ? (
                      <CloudOff
                        className={`w-5 h-5 ${
                          isActive ? 'text-primary-600 dark:text-primary-400' : 'text-text-secondary-light dark:text-text-secondary-dark'
                        }`}
                      />
                    ) : (
                      <Cloud
                        className={`w-5 h-5 ${
                          isActive ? 'text-primary-600 dark:text-primary-400' : 'text-text-secondary-light dark:text-text-secondary-dark'
                        }`}
                      />
                    )}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                          {provider.name}
                        </h3>
                        {isActive && <Check className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
                      </div>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                        {provider.description}
                      </p>
                    </div>

                    {needsApiKey && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                          {provider.name} API Token
                        </label>
                        <PasswordInput
                          value={providerApiKey}
                          onChange={(e) => setProviderApiKey(e.target.value)}
                          placeholder={`Enter your ${provider.name} API token`}
                          className="w-full px-3 py-2 border border-border-hover-light dark:border-border-hover-dark bg-surface-raised-light dark:bg-surface-hover-dark text-text-primary-light dark:text-text-primary-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                        />
                        {getProviderApiKeyHelpUrl(provider.id) && (
                          <p className="text-xs text-text-tertiary-light dark:text-text-secondary-dark">
                            Get your API token from{' '}
                            <a
                              href={getProviderApiKeyHelpUrl(provider.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {provider.name} Settings
                            </a>
                          </p>
                        )}
                      </div>
                    )}

                    {!isActive && (
                      <button
                        onClick={() => handleToggleSync(provider.id)}
                        disabled={isSaving || (!isStandalone && !providerApiKey.trim())}
                        className="w-full px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSaving ? (
                          <><Loader2 className="w-4 h-4 animate-spin" />Enabling...</>
                        ) : (
                          <>
                            {isStandalone ? <CloudOff className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
                            {isStandalone ? 'Switch to Standalone' : `Enable ${provider.name} Sync`}
                          </>
                        )}
                      </button>
                    )}

                    {isActive && !isStandalone && (
                      <button
                        onClick={handleSyncNow}
                        disabled={isSyncing}
                        className="w-full px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSyncing ? (
                          <><Loader2 className="w-4 h-4 animate-spin" />Syncing...</>
                        ) : (
                          <><RefreshCw className="w-4 h-4" />Sync Now</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Views Management Section */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Views
          </h2>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Manage your dashboard views. Hide default views or delete custom views.
        </p>

        <div className="space-y-2">
          {views.map((view) => {
            const ViewIcon = getViewIconComponent(view);
            return (
              <div
                key={view.slug}
                className="p-3 bg-surface-base-light dark:bg-surface-hover-dark/50 rounded-lg space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      {view.isDefault ? (
                        <div className="p-1.5 rounded-md bg-surface-hover-light dark:bg-surface-hover-dark">
                          <ViewIcon className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setIconPickerViewSlug(iconPickerViewSlug === view.slug ? null : view.slug)
                          }
                          className="p-1.5 rounded-md bg-surface-hover-light dark:bg-surface-hover-dark hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:ring-2 hover:ring-primary-500/50 transition-all"
                          title="Change icon"
                        >
                          <ViewIcon className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                        </button>
                      )}
                      {!view.isDefault && iconPickerViewSlug === view.slug && (
                        <ViewIconPicker
                          currentIconName={view.filterConfig?.icon}
                          onSelect={(iconName) => handleIconSelect(view, iconName)}
                          onClose={() => setIconPickerViewSlug(null)}
                        />
                      )}
                    </div>
                    <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                      {view.name}
                    </span>
                    {view.isDefault && (
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {view.isDefault ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleHideView(view.slug);
                        }}
                        className="p-2 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
                        title={isViewHidden(view.slug) ? 'Show view' : 'Hide view'}
                      >
                        {isViewHidden(view.slug) ? (
                          <EyeOff className="w-4 h-4 text-text-tertiary-light dark:text-text-tertiary-dark" />
                        ) : (
                          <Eye className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewToDelete(view);
                        }}
                        className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        title="Delete view"
                      >
                        <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="text-xs font-mono text-text-secondary-light dark:text-text-secondary-dark bg-surface-raised-light dark:bg-surface-raised-dark rounded p-2 overflow-x-auto">
                  <div className="mb-1 text-text-tertiary-light dark:text-text-tertiary-dark">
                    Slug: {view.slug}
                  </div>
                  {view.filterConfig && Object.keys(view.filterConfig).length > 0 ? (
                    <pre className="whitespace-pre-wrap break-words">
                      {JSON.stringify(view.filterConfig, null, 2)}
                    </pre>
                  ) : (
                    <span className="text-text-tertiary-light dark:text-text-tertiary-dark italic">
                      No filter configured
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {views.length === 0 && (
            <p className="text-sm text-text-tertiary-light dark:text-text-secondary-dark text-center py-4">
              No views available. Ask Danny to create custom views!
            </p>
          )}
        </div>
      </div>

      {/* Maintenance Section */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
          Maintenance
        </h2>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Clear cached data to free up space or fix issues.
        </p>

        <button
          onClick={handleClearCache}
          className="w-full px-4 py-2 bg-surface-hover-light dark:bg-surface-hover-dark text-text-primary-light dark:text-text-primary-dark rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors flex items-center justify-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Clear Local Cache
        </button>
      </div>

      {/* Danger Zone */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-red-200 dark:border-red-900/50 rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">
          Danger Zone
        </h2>
        <div className="space-y-1">
          <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
            Cull Stale Backlog
          </p>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            Review and remove tasks that have been sitting untouched for months. This action is irreversible — use with care.
          </p>
        </div>
        <button
          onClick={() => setIsCullOpen(true)}
          className="w-full px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Cull Stale Backlog
        </button>
      </div>

      {/* Cull Backlog Modal */}
      {isCullOpen && (
        <CullBacklogModal
          onClose={() => setIsCullOpen(false)}
          onComplete={() => {
            setIsCullOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}

      {/* Merge Modal */}
      {showMergeModal && orphans && (
        <MergeConflictsModal
          orphans={orphans}
          onResolve={handleMergeDecisions}
          onCancel={() => {
            setShowMergeModal(false);
            setOrphans(null);
            setIsSaving(false);
          }}
        />
      )}

      {/* Delete View Modal */}
      {viewToDelete && (
        <DeleteViewConfirmModal
          viewName={viewToDelete.name}
          onConfirm={handleDeleteView}
          onCancel={() => setViewToDelete(null)}
          isDeleting={isDeletingView}
        />
      )}
    </>
  );
}
